import {
  ColumnType,
  DoctorDetailType,
  ExpectedRowTypes,
  TestKitsListType,
  TestKitType,
  UserType,
} from "@/common/types";
import CardsForTable from "@/components/cards-for-table";
import KpiCard from "@/components/kpiCard";
import Table from "@/components/table";
import { Button } from "@/components/ui/button";
import { getTestsKits } from "@/redux/actions/testKitActions";
import { getDoctorDetailById } from "@/redux/actions/usersActions";
import { RootState } from "@/redux/reducers";
import {
  capitalize,
  isUserAdmin,
  transformKitStatus,
} from "@/utils/common.util";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import VisualizationAccess from "../workflows/visualization-access";

const columns: ColumnType[] = [
  {
    field: "kit_id",
    headerName: "Kit ID",
  },
  {
    field: "assigned_patient",
    headerName: "Patient",
    render: (row) => (row as TestKitType).assigned_patient?.name || "-",
  },
  {
    field: "status",
    headerName: "Status",
    render: (row) => transformKitStatus((row as TestKitType).status),
  },
];

const DoctorsView = ({ user }: { user: UserType }) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const { doctorDetails } = useSelector((state: RootState) => state.users);
  const { testKits } = useSelector((state: RootState) => state.testKit);

  useEffect(() => {
    dispatch(getDoctorDetailById(user?.id as number));
    dispatch(getTestsKits(1, { doctor: `${user?.id}` }, 5));
  }, [dispatch, user.id]);

  const handleRowClick = (
    e: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
    row: ExpectedRowTypes
  ) => {
    e.stopPropagation();
    e.preventDefault();
    navigate(`/test/${row.id}`);
  };

  return (
    <div>
      {isUserAdmin() && (
        <div className="flex justify-end">
          <Button variant="secondary">
            <VisualizationAccess
              doctorId={user.id as number}
              hasVisualizationAccess={
                (doctorDetails as DoctorDetailType)?.has_visualization_access
              }
              callback={() => {
                dispatch(getDoctorDetailById(user?.id as number));
              }}
            />
          </Button>
        </div>
      )}
      <div className="text-3xl font-bold">{user.name}</div>
      <div className="text-gray-400 text-xs">({capitalize(user.role)})</div>
      <div className="text-gray-400 text-xs">{user.email}</div>
      <div className="flex gap-2 md:gap-4 mt-4 flex-col md:flex-row">
        <KpiCard
          label="Patient Count"
          value={(doctorDetails as DoctorDetailType)?.patient_count}
        />
        <KpiCard
          label="Total Assigned Kits"
          value={
            (doctorDetails as DoctorDetailType)?.test_kit_count
              ?.total_kits_assigned
          }
        />
        <KpiCard
          label="Unused Kits"
          value={
            (doctorDetails as DoctorDetailType)?.test_kit_count?.unused_kits
          }
        />
      </div>
      <div className="mt-8 md:mt-4">
        <div className="text-black font-semibold text-base mb-2">
          Test Kit History
        </div>

        {/* Desktop View */}
        <div className="hidden md:flex">
          <Table
            rows={(testKits as TestKitsListType)?.results || []}
            columns={columns}
            rowsPerPage={5}
            onRowClick={(e, row) =>
              handleRowClick(
                e as React.MouseEvent<HTMLTableRowElement, MouseEvent>,
                row
              )
            }
          />
        </div>

        {/* Mobile View */}
        <div className="flex md:hidden">
          <CardsForTable
            rows={(testKits as TestKitsListType)?.results || []}
            columns={columns}
            rowsPerPage={5}
            onRowClick={(e, row) =>
              handleRowClick(
                e as React.MouseEvent<HTMLTableRowElement, MouseEvent>,
                row
              )
            }
          />
        </div>
      </div>
    </div>
  );
};

export default DoctorsView;
